import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/** How long the "Added" success state stays before the card resets. */
const ADDED_STATE_DURATION = 2000;

/**
 * Inline Quick Add Component
 *
 * Handles inline variant selection and add-to-cart on product cards without a modal.
 *
 * Scenarios:
 * 1. Single variant: the "+" button adds directly to cart.
 * 2. One option with multiple values: the option row is shown directly; picking a value adds.
 * 3. Multiple options: options are revealed one row at a time (sequential). In grid/search
 *    placements (`data-skip-plus-step`) the first row auto-shows in landscape and is gated
 *    behind "+" in portrait; in carousels the flow always starts from "+". Once every
 *    multi-value option is chosen, the matching variant is added.
 *
 * Overlay visibility is driven by the `hidden` attribute toggled here; the "+" button
 * is hidden via CSS whenever any overlay is visible.
 *
 * @typedef {object} QuickAddInlineRefs
 * @property {HTMLButtonElement} plusButton - The main "+" button
 * @property {HTMLDivElement} overlaysContainer - Container for option overlays
 *
 * @extends Component<QuickAddInlineRefs>
 */
export class QuickAddInlineComponent extends Component {
  requiredRefs = ['plusButton', 'overlaysContainer'];

  /** @type {Array<{name: string, position: number, values: Array<{value: string, available: boolean}>}>} */
  #options = [];

  /** @type {Array<{id: number, option1: string|null, option2: string|null, option3: string|null, available: boolean}>} */
  #variants = [];

  /** @type {string[]} */
  #selectedOptions = [];

  /** Indices (into #options) of options that have more than one value. */
  /** @type {number[]} */
  #choiceIndices = [];

  /** @type {boolean} */
  #isLoading = false;

  /** Whether the shopper has begun interacting (clicked "+" or picked a value). */
  #userStarted = false;

  /** @type {AbortController|null} */
  #abortController = null;

  /** Whether the document-level outside-click listener is currently attached. */
  #isListeningForOutsideClick = false;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #resetTimeout;

  connectedCallback() {
    super.connectedCallback();
    this.#syncProductData();

    this.refs.plusButton.addEventListener('click', this.#handlePlusClick);

    this.addEventListener('change', this.#handleOptionSelect);

    this.refs.overlaysContainer.addEventListener('click', this.#stopPropagation);

    this.#applyInitialState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);
    this.#stopListeningForOutsideClick();
  }

  #syncProductData() {
    const variantsScript = this.querySelector('script[data-variants]');
    const optionsScript = this.querySelector('script[data-options]');

    try { this.#variants = JSON.parse(variantsScript?.textContent || '[]'); } catch { this.#variants = []; }
    try { this.#options = JSON.parse(optionsScript?.textContent || '[]'); } catch { this.#options = []; }

    this.#choiceIndices = this.#options
      .map((option, index) => (option.values.length > 1 ? index : -1))
      .filter((index) => index >= 0);
  }

  /** @returns {HTMLElement[]} The option overlays (excludes the success overlay). */
  get #optionOverlays() {
    return Array.from(this.refs.overlaysContainer.querySelectorAll('.quick-add-inline__overlay[data-option-index]'));
  }

  /** @returns {HTMLElement|null} */
  get #successOverlay() {
    return this.refs.overlaysContainer.querySelector('.quick-add-inline__overlay--success');
  }

  /**
   * @returns {boolean}
   */
  #isSkipContext() {
    return this.hasAttribute('data-skip-plus-step');
  }

  #hideAllOverlays() {
    for (const overlay of this.#optionOverlays) overlay.hidden = true;
    if (this.#successOverlay) this.#successOverlay.hidden = true;
  }

  /** @param {number} index - Reveals the option overlay with this option index. */
  #revealOption(index) {
    for (const overlay of this.#optionOverlays) {
      overlay.hidden = Number(overlay.dataset.optionIndex) !== index;
    }
    if (this.#successOverlay) this.#successOverlay.hidden = true;
  }

  #showSuccess() {
    this.#stopListeningForOutsideClick();
    delete this.dataset.started;
    for (const overlay of this.#optionOverlays) overlay.hidden = true;
    if (this.#successOverlay) this.#successOverlay.hidden = false;
  }

  #markStarted() {
    this.#userStarted = true;
    this.dataset.started = '';
  }

  #applyInitialState() {
    this.#userStarted = false;
    this.#selectedOptions = [];
    delete this.dataset.started;
    this.#stopListeningForOutsideClick();

    for (const input of this.querySelectorAll('input[data-option-value]')) {
      if (input instanceof HTMLInputElement) input.checked = false;
    }

    this.#hideAllOverlays();
    this.#refreshAvailability();

    if (this.dataset.singleVariant === 'true' || this.#choiceIndices.length === 0) return;

    const firstChoice = this.#choiceIndices[0];
    if (firstChoice == null) return;

    if (this.#isSkipContext()) {
      this.#revealOption(firstChoice);
    }
  }

  #handlePlusClick = async (/** @type {Event} */ event) => {
    event.preventDefault();
    event.stopPropagation();

    if (this.#isLoading) return;

    this.#syncProductData();

    if (this.dataset.singleVariant === 'true') {
      const variantId = this.dataset.variantId;
      if (variantId) await this.#addToCart(variantId);
      return;
    }

    // Multi-option: "+" starts the sequence by revealing the first option row.
    const firstChoice = this.#choiceIndices[0];
    if (firstChoice == null) return;
    this.#markStarted();
    this.#revealOption(firstChoice);
    this.#startListeningForOutsideClick();
  };

  #handleOptionSelect = async (/** @type {Event} */ event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.dataset.optionValue) return;

    event.stopPropagation();
    this.#syncProductData();

    const optionValue = input.dataset.optionValue;
    const optionIndex = parseInt(input.dataset.optionIndex || '0', 10);

    this.#markStarted();
    this.#selectedOptions[optionIndex] = optionValue;
    this.#refreshAvailability();
    this.#startListeningForOutsideClick();

    // Reveal the next unselected multi-value option, if any.
    const nextIndex = this.#choiceIndices.find((index) => !this.#selectedOptions[index]);
    if (nextIndex != null) {
      this.#revealOption(nextIndex);
      return;
    }

    const variant = this.#findVariantByOptions();
    if (variant?.available) await this.#addToCart(String(variant.id));
  };

  #refreshAvailability() {
    for (const input of this.querySelectorAll('input[data-option-value]')) {
      if (!(input instanceof HTMLInputElement)) continue;

      const optionIndex = parseInt(input.dataset.optionIndex || '0', 10);
      const optionValue = input.dataset.optionValue || '';

      const tentative = [...this.#selectedOptions];
      tentative[optionIndex] = optionValue;

      const available = this.#variants.some((variant) => {
        if (!variant.available) return false;
        const optionValues = [variant.option1, variant.option2, variant.option3];
        return tentative.every((value, i) => value == null || value === '' || optionValues[i] === value);
      });

      input.disabled = !available;
      input.dataset.optionAvailable = String(available);
      if (available) {
        input.removeAttribute('aria-disabled');
      } else {
        input.setAttribute('aria-disabled', 'true');
      }
    }
  }

  /**
   * @returns {{id: number, available: boolean}|undefined}
   */
  #findVariantByOptions() {
    const selected = this.#options.map((option, i) => {
      if (this.#selectedOptions[i]) return this.#selectedOptions[i];
      const singleValue = option.values[0];
      if (option.values.length === 1 && singleValue) return singleValue.value;
      return null;
    });

    return this.#variants.find((variant) => {
      const optionValues = [variant.option1, variant.option2, variant.option3];
      return selected.every((value, i) => value === null || optionValues[i] === value);
    });
  }

  /**
   * @returns {string[]}
   */
  #cartSectionIds() {
    const ids = new Set();
    for (const item of document.querySelectorAll('cart-items-component')) {
      if (item instanceof HTMLElement && item.dataset.sectionId) ids.add(item.dataset.sectionId);
    }
    return Array.from(ids);
  }

  /**
   * @param {string} variantId
   */
  async #addToCart(variantId) {
    if (this.#isLoading) return;

    this.#isLoading = true;
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    this.classList.add('quick-add-inline--loading');

    try {
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');

      const sectionIds = this.#cartSectionIds();
      if (sectionIds.length > 0) {
        formData.append('sections', sectionIds.join(','));
        formData.append('sections_url', window.location.pathname);
      }

      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchConfig('javascript', { body: formData }),
        signal: this.#abortController.signal,
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to add to cart');

      document.dispatchEvent(
        new CartAddEvent({}, variantId, {
          source: 'product-form-component',
          itemCount: 1,
          productId: this.dataset.productId,
          sections: data.sections,
        })
      );

      // Reveal the success overlay, then reset back to the resting state.
      this.dataset.state = 'added';
      this.#showSuccess();
      this.#scheduleReset();
    } catch (error) {
      if (error.name !== 'AbortError') delete this.dataset.state;
    } finally {
      this.#isLoading = false;
      this.classList.remove('quick-add-inline--loading');
      this.#abortController = null;
    }
  }

  #scheduleReset() {
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);
    this.#resetTimeout = setTimeout(() => {
      delete this.dataset.state;
      this.#applyInitialState();
    }, ADDED_STATE_DURATION);
  }

  #stopPropagation = (/** @type {Event} */ event) => {
    event.stopPropagation();
  };

  /**
   * @param {Event} event
   */
  #handleOutsideClick = (event) => {
    const target = event.target;
    if (target instanceof Node && this.contains(target)) return;
    // Don't interrupt an in-flight add or the success state.
    if (this.#isLoading || this.dataset.state === 'added') return;

    this.#applyInitialState();
  };

  #startListeningForOutsideClick() {
    if (this.#isListeningForOutsideClick) return;
    document.addEventListener('click', this.#handleOutsideClick);
    this.#isListeningForOutsideClick = true;
  }

  #stopListeningForOutsideClick() {
    if (!this.#isListeningForOutsideClick) return;
    document.removeEventListener('click', this.#handleOutsideClick);
    this.#isListeningForOutsideClick = false;
  }
}

if (!customElements.get('quick-add-inline-component')) {
  customElements.define('quick-add-inline-component', QuickAddInlineComponent);
}
